<template>
	<div class="RefusedProductsEditor" v-if="canEditRefused">
		<h4>Refused Products</h4>
		<p class="hint">
			Products in this list will not have DB metadata or Fx/Media synced to this agent.
			An optional expiration date may be set; the product is automatically un-refused when the agent's local date reaches that date.
		</p>
		<div v-if="editRefusedList.length === 0" class="noneRefused">No products are currently refused.</div>
		<table v-else class="refusedTable">
			<thead>
				<tr><th>Product</th><th>Expires (optional)</th><th></th></tr>
			</thead>
			<tbody>
				<tr v-for="row in sortedEditRefusedList" :key="'ref' + row.productRootId">
					<td><span v-html="productNameHtml(row.productRootId)"></span> <span class="rootId">{{productIdsLabel(row.productRootId)}}</span></td>
					<td><input type="date" :min="todayString" v-model="row.expirationDate" /></td>
					<td><button class="removeBtn" @click="removeRefusedProduct(row.productRootId)">Remove</button></td>
				</tr>
			</tbody>
		</table>
		<div class="addRefused">
			<input type="text" v-model="addQuery" placeholder="Search by name, cms ID, or fx ID…" class="addSearch" />
			<div v-if="addQuery" class="addResults">
				<div v-if="addableProducts.length === 0" class="noResults">No matching products.</div>
				<button v-for="proot in addableProducts" :key="'add' + proot.ProductRootId" class="addResultBtn" @click="addRefusedProduct(proot)">
					<span v-html="proot.InternalNameHtml"></span> <span class="rootId">{{formatProductIds(proot)}}</span>
				</button>
			</div>
		</div>
		<div class="commitRow">
			<button class="commitBtn" :disabled="committing" @click="commitRefusedProducts">{{committing ? 'Saving…' : 'Commit Refused Products'}}</button>
			<span v-if="commitMessage" class="commitMessage">{{commitMessage}}</span>
			<span v-if="commitError" class="commitError">{{commitError}}</span>
		</div>
	</div>
</template>

<script>
	import { CMSSetRefusedProducts } from 'appRoot/api/CMSUserData';
	import { HTMLToText } from 'appRoot/scripts/Util';

	export default {
		props:
		{
			agentId: {
				type: String,
				required: true
			},
			agentStatus: {
				type: Object,
				required: true
			},
			productRoots: {
				type: Array,
				default: null
			}
		},
		data()
		{
			return {
				editRefusedList: [],
				addQuery: "",
				committing: false,
				commitError: null,
				commitMessage: null,
			};
		},
		created()
		{
			this.initRefusedEdit();
		},
		computed:
		{
			rootMap()
			{
				let map = {};
				let roots = this.productRoots;
				if (roots)
				{
					for (let i = 0; i < roots.length; i++)
						map[roots[i].ProductRootId] = roots[i];
				}
				return map;
			},
			canEditRefused()
			{
				let ud = this.$store.state.userData;
				return !!(ud && ud.CMSWriteAccess) && !!this.agentStatus && this.agentStatus.connected && this.agentStatus.CMSRole !== 'None';
			},
			sortedEditRefusedList()
			{
				// Returns the same row object references (so v-model continues to bind to editRefusedList entries), sorted by product name.
				let rootMap = this.rootMap;
				return [...this.editRefusedList].sort((a, b) =>
				{
					let na = this.productNameText(a.productRootId, rootMap);
					let nb = this.productNameText(b.productRootId, rootMap);
					return na < nb ? -1 : (na > nb ? 1 : 0);
				});
			},
			addableProducts()
			{
				let query = this.addQuery.trim().toLowerCase();
				if (!query)
					return [];
				let alreadyRefused = {};
				for (let i = 0; i < this.editRefusedList.length; i++)
					alreadyRefused[this.editRefusedList[i].productRootId] = true;
				let results = [];
				let roots = this.productRoots || [];
				for (let i = 0; i < roots.length; i++)
				{
					let proot = roots[i];
					if (alreadyRefused[proot.ProductRootId])
						continue;
					if (this.productMatchesQuery(proot, query))
					{
						results.push(proot);
						if (results.length >= 30)
							break;
					}
				}
				return results;
			},
			todayString()
			{
				let d = new Date();
				let m = ('0' + (d.getMonth() + 1)).slice(-2);
				let day = ('0' + d.getDate()).slice(-2);
				return d.getFullYear() + '-' + m + '-' + day;
			}
		},
		methods:
		{
			formatProductIds(proot)
			{
				if (!proot)
					return "";
				let label = "cms" + proot.ProductRootId;
				if (proot.TaylorProductId)
					label += " fx" + proot.TaylorProductId;
				return label;
			},
			productIdsLabel(productRootId)
			{
				let proot = this.rootMap[productRootId];
				if (proot)
					return this.formatProductIds(proot);
				return "cms" + productRootId;
			},
			productMatchesQuery(proot, query)
			{
				let name = HTMLToText(proot.InternalNameHtml || "").toLowerCase();
				if (name.indexOf(query) !== -1)
					return true;

				let cmsId = "" + proot.ProductRootId;
				let fxId = proot.TaylorProductId ? ("" + proot.TaylorProductId) : "";
				let cmsLabel = "cms" + cmsId;
				let fxLabel = fxId ? ("fx" + fxId) : "";

				if (cmsId.indexOf(query) !== -1 || cmsLabel.indexOf(query) !== -1)
					return true;
				if (fxId && (fxId.indexOf(query) !== -1 || fxLabel.indexOf(query) !== -1))
					return true;

				// Allow queries like "cms1166" / "fx1098" (and with a space after the prefix).
				if (query.indexOf("cms") === 0)
				{
					let cmsQuery = query.slice(3).trim();
					if (cmsQuery && cmsId.indexOf(cmsQuery) !== -1)
						return true;
				}
				else if (query.indexOf("fx") === 0)
				{
					let fxQuery = query.slice(2).trim();
					if (fxQuery && fxId && fxId.indexOf(fxQuery) !== -1)
						return true;
				}
				return false;
			},
			productNameText(productRootId, rootMap)
			{
				let map = rootMap || this.rootMap;
				let proot = map[productRootId];
				return proot ? HTMLToText(proot.InternalNameHtml || "").toLowerCase() : "";
			},
			productNameHtml(productRootId)
			{
				let proot = this.rootMap[productRootId];
				return proot ? proot.InternalNameHtml : ('Unknown product cms' + productRootId);
			},
			initRefusedEdit()
			{
				let list = [];
				if (this.agentStatus && this.agentStatus.refusedProductRootIds)
				{
					let exp = this.agentStatus.refusedProductExpirations || {};
					for (let i = 0; i < this.agentStatus.refusedProductRootIds.length; i++)
					{
						let id = this.agentStatus.refusedProductRootIds[i];
						list.push({ productRootId: id, expirationDate: exp[id] || "" });
					}
				}
				this.editRefusedList = list;
				this.commitError = null;
				this.commitMessage = null;
			},
			addRefusedProduct(proot)
			{
				if (!this.editRefusedList.some(r => r.productRootId === proot.ProductRootId))
					this.editRefusedList.push({ productRootId: proot.ProductRootId, expirationDate: "" });
				this.addQuery = "";
			},
			removeRefusedProduct(productRootId)
			{
				let idx = this.editRefusedList.findIndex(r => r.productRootId === productRootId);
				if (idx !== -1)
					this.editRefusedList.splice(idx, 1);
			},
			commitRefusedProducts()
			{
				if (this.committing)
					return;
				this.committing = true;
				this.commitError = null;
				this.commitMessage = null;

				let ids = this.editRefusedList.map(r => r.productRootId);
				let expirations = {};
				for (let i = 0; i < this.editRefusedList.length; i++)
				{
					let r = this.editRefusedList[i];
					if (r.expirationDate)
						expirations[r.productRootId] = r.expirationDate;
				}

				CMSSetRefusedProducts(this.agentId, ids, expirations)
					.then(data =>
					{
						if (data.success)
						{
							this.commitMessage = "Saved. The agent is reconnecting to apply the change…";
							// The agent reconnects ~1 second after responding. Reload after a short delay to reflect the refreshed state.
							setTimeout(() =>
							{
								this.$emit('reload');
							}, 4000);
						}
						else
							this.commitError = data.error;
					})
					.catch(err =>
					{
						this.commitError = err;
					})
					.finally(() =>
					{
						this.committing = false;
					});
			}
		},
		watch:
		{
			agentStatus()
			{
				// Re-initialize the editing list whenever the parent reloads and provides a fresh status object.
				this.initRefusedEdit();
			}
		}
	}
</script>

<style scoped>
	.RefusedProductsEditor
	{
		border: 1px solid #cccccc;
		border-radius: 6px;
		padding: 0.5em 0.75em 0.75em;
		margin: 0.5em 0 1em;
		background-color: rgba(0,0,0,0.02);
	}

		.RefusedProductsEditor h4
		{
			margin-top: 0.25em;
		}

		.RefusedProductsEditor .hint
		{
			font-size: 0.9em;
			color: #555555;
			margin: 0.25em 0 0.75em;
		}

		.RefusedProductsEditor .noneRefused
		{
			font-style: italic;
			color: #555555;
			margin-bottom: 0.5em;
		}

	.refusedTable
	{
		border-collapse: collapse;
		width: 100%;
		margin-bottom: 0.75em;
	}

		.refusedTable th,
		.refusedTable td
		{
			text-align: left;
			padding: 4px 8px;
			border-bottom: 1px solid #e0e0e0;
			vertical-align: middle;
		}

		.refusedTable th
		{
			font-size: 0.85em;
			color: #555555;
		}

	.rootId
	{
		color: #888888;
		font-size: 0.85em;
	}

	.removeBtn
	{
		cursor: pointer;
	}

	.addRefused
	{
		position: relative;
		margin-bottom: 0.75em;
	}

	.addSearch
	{
		width: 100%;
		box-sizing: border-box;
		padding: 6px 8px;
	}

	.addResults
	{
		border: 1px solid #cccccc;
		border-radius: 4px;
		max-height: 220px;
		overflow-y: auto;
		margin-top: 2px;
		background-color: #ffffff;
	}

		.addResults .noResults
		{
			padding: 6px 8px;
			color: #888888;
			font-style: italic;
		}

	.addResultBtn
	{
		display: block;
		width: 100%;
		text-align: left;
		padding: 6px 8px;
		border: none;
		border-bottom: 1px solid #eeeeee;
		background-color: transparent;
		cursor: pointer;
	}

		.addResultBtn:hover,
		.addResultBtn:focus
		{
			background-color: rgba(0,0,0,0.08);
		}

	.commitRow
	{
		display: flex;
		align-items: center;
		gap: 0.75em;
		flex-wrap: wrap;
	}

	.commitBtn
	{
		padding: 6px 12px;
		cursor: pointer;
	}

		.commitBtn:disabled
		{
			cursor: default;
			opacity: 0.6;
		}

	.commitMessage
	{
		color: #007700;
	}

	.commitError
	{
		color: #AA0000;
		font-weight: bold;
	}
</style>
